import { CdkDragEnd, CdkDragStart, DragDropModule } from '@angular/cdk/drag-drop';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Optional, signal, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { SplitAreaComponent, SplitComponent } from 'angular-split';
import { forkJoin, Subscription } from 'rxjs';
import { FormPreviewComponent } from '../../../common-shared-module/components/form-preview/form-preview.component';
import { PageScrollContentComponent } from '../../../common-shared-module/components/page-scroll-content/page-scroll-content.component';
import { DynFormBase } from '../../../common-shared-module/model/dyn-form/dyn-form.model';
import { ItemTypeMetadata, ToItemTypeMetadata } from '../../../common-shared-module/model/dyn-form/form-metadata/item-type-metadata.enum';
import { ExtendedLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/extends-layout.metadata.model';
import { RepeatLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/repeat-layout-metadata.model';
import { RowLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/row-layout.metadata.model';
import { SectionLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/section-layout.model';
import { TableLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/table-layout-metadata.model';
import { ExtendedProcedureForm } from '../../../common-shared-module/model/procedure/extended-procedure-form.model';
import { ProcedureFormFilterBase } from '../../../common-shared-module/model/procedure/procedure-form-filter.model';
import { DynFormService } from '../../../common-shared-module/services/dyn-form/dyn-form.service';
import { ProcedureFormService } from '../../../common-shared-module/services/procedure/procedure-form.service';
import { SpinnerService } from '../../../common-shared-module/services/spinner/spinner.service';
import { FormEditorSectionHeaderComponent } from '../../components/form-editor-section-header/form-editor-section-header.component';
import { FormEditorSectionLayoutComponent } from '../../components/form-editor-section-layout/form-editor-section-layout.component';
import { FormEditorSectionPreviewComponent } from '../../components/form-editor-section-preview/form-editor-section-preview.component';
import { FormEditorSectionSelectorComponent } from '../../components/form-editor-section-selector/form-editor-section-selector.component';
import { FormEditorSectionToolsComponent } from '../../components/form-editor-section-tools/form-editor-section-tools.component';
import { DDElement } from '../../models/dd-element.model';
import { MyToolFactory } from '../../models/dd-my-tool.model';
import { HistoryChangeBuilder } from '../../models/history-change.model';
import { Layout } from '../../models/layout.model';
import { MetadataChange } from '../../models/metadata-change.model';
import { RepeatLayout } from '../../models/repeat-layout.model';
import { RequestedAddTool } from '../../models/requested-add-tool.model';
import { RowLayout } from '../../models/row-layout.model';
import { SelectedLayout } from '../../models/selected-layout.model';
import { SelectedRow } from '../../models/selected-row.model';
import { SelectedWidget } from '../../models/selected-widget.model';
import { TableLayout } from '../../models/table-layout.model';
import { DdSupportService } from '../../services/dd-support/dd-support.service';
import { SectionHistoryService } from '../../services/history/section-history.service';
import { SectionMetadataService } from '../../services/section-metadata/section-metadata.service';
import { FormlyConfig } from '@ngx-formly/core';
import { WebServiceLayout } from '../../models/webservice-layout.model';
import { WebServiceLayoutMetadataBase } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/webservice-layout-metadata.model';
import { LayoutTypeMetadata } from '../../../common-shared-module/model/dyn-form/form-metadata/layout-type-metadata.enum';

@Component({
    selector: 'app-form-editor',
    templateUrl: './form-editor.component.html',
    styleUrl: './form-editor.component.scss',
    standalone: true,
    imports: [
    FormEditorSectionHeaderComponent, FormEditorSectionToolsComponent, FormEditorSectionLayoutComponent, FormEditorSectionPreviewComponent,
    PageScrollContentComponent,
    SplitComponent, SplitAreaComponent,
    MatSnackBarModule, MatIconModule,
    CommonModule, DragDropModule,
]
})
export class FormEditorComponent implements OnInit, OnDestroy {

    @ViewChild(FormEditorSectionHeaderComponent) headerComponent!: FormEditorSectionHeaderComponent
    @ViewChild(FormEditorSectionPreviewComponent) previewComponent!: FormEditorSectionPreviewComponent
    @ViewChild(FormEditorSectionLayoutComponent) layoutComponent!: FormEditorSectionLayoutComponent
    @ViewChild(FormEditorSectionToolsComponent) detailComponent!: FormEditorSectionToolsComponent

    procedureFormId!: number;
    currentSectionId!: number;

    procedureForm = signal<ExtendedProcedureForm | null>(null)
    dynForm!: DynFormBase

    viewInitiated: boolean = false
    ddWidget: boolean = false
    registryHistory: boolean = true

    loaded = signal(false)
    loadingHttp = signal(false)

    spinnerSubscription: Subscription

    constructor(
        private location: Location, private activatedRoute: ActivatedRoute,
        private snack: MatSnackBar, private overlay: Overlay, protected dialog: MatDialog,

        public ddSupportService: DdSupportService,
        public sectionService: SectionMetadataService, public sectionHistoryService: SectionHistoryService,
        public dynFormService: DynFormService, public procedureFormService: ProcedureFormService,
        public spinnerService: SpinnerService,

        private readonly changeDetectorRef: ChangeDetectorRef
        
    ) {
        this.spinnerSubscription = this.spinnerService.subscribe((loading) => { this.loadingHttp.update(() => loading) })
    }

    ngOnInit(): void {

        this.activatedRoute.queryParamMap.subscribe(qp => {

            this.loaded.update(() => false)

            this.sectionHistoryService.clear()
            this.headerComponent?.refresh()

            let procedureFormId = qp.get("procedureFormId")
            let sectionId = qp.get("sectionId")

            if (procedureFormId == null) {
                this.snack.open("Error recuperant la definició del formulari.")
                return
            }

            this.procedureFormId = parseInt(procedureFormId)

            let procedureFilter: ProcedureFormFilterBase = new ProcedureFormFilterBase()
            procedureFilter.id = this.procedureFormId

            forkJoin({
                dynForm: this.dynFormService.findDynFormByProcedureFormId(this.procedureFormId),
                procedureForm: this.procedureFormService.searchProcedure(procedureFilter, true)
            }).subscribe(r => {

                if (r == null || !r.dynForm || !r.dynForm.ok || !r.procedureForm || r.procedureForm.totalElements != 1) {
                    // TODO - Display Error loading form //
                    this.previewComponent.clean()
                    return
                }

                let dynFormSection = r.dynForm.entity.sections[0]

                if (sectionId != null) {
                    let sectionFound = r.dynForm.entity.sections.find(s => s.id === parseInt(sectionId))
                    if (sectionFound) {
                        dynFormSection = sectionFound
                    } else {
                        // TODO - Display Error loading form //
                        this.previewComponent.clean()
                        return
                    }
                }

                this.currentSectionId = dynFormSection.id

                this.procedureForm.update(() => r.procedureForm.content[0])
                this.dynForm = r.dynForm.entity
                this.dynForm.procedureFormId = this.procedureFormId

                this.dynFormService.loadSection(this.currentSectionId).subscribe(sectionLoad => {

                    this.loaded.update(() => true)
                    this.sectionService.init()

                    let section = this.sectionService.sectionMetadata
                    if (sectionLoad != null) {
                        section = sectionLoad
                    } else {
                        this.sectionService.sectionMetadata.name = dynFormSection.name
                    }

                    this.sectionHistoryService.init(new HistoryChangeBuilder().section(section).sectionForm(dynFormSection).build())
                    let state = this.sectionHistoryService.currentState()
                    if (state) {
                        this.sectionService.sectionMetadata = state
                        this.restoreState(state)
                    } else {
                        // TODO - Display error loading form 
                        this.snack.open("Error recuperant la definició del formulari.")
                        this.previewComponent.clean()
                        return
                    }

                    this.viewInitiated = true
                    this.previewComponent.refreshAll(this.sectionService.sectionMetadata)
                    this.headerComponent.refresh()
                    this.changeDetectorRef.detectChanges()
                })

            })
        })

    }

    // TODO - Go To Angular12 Procedure URL //
    goBack(): void {
        this.location.go("/procedure")
    }

    showSectionSelector(selector: ElementRef): void {

        if (this.sectionHistoryService.currentHistoryChange == null) return

        let fc = this.overlay.position().flexibleConnectedTo(selector).withPositions([
            { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }
        ])

        const overlayRef = this.overlay.create({ hasBackdrop: true, positionStrategy: fc, backdropClass: 'non-exists' })

        const addSectionPortal = new ComponentPortal<FormEditorSectionSelectorComponent>(FormEditorSectionSelectorComponent)

        let attach = overlayRef.attach(addSectionPortal)
        attach.instance.dynForm = this.dynForm
        attach.instance.dynFormSection = this.sectionHistoryService.currentHistoryChange.sectionForm()
        attach.instance.overlayRef = overlayRef

        overlayRef.backdropClick().subscribe(() => overlayRef.dispose())

    }

    showPreview(): void {
        if (this.procedureFormId) {
            // Remove focus before open dialog for Accessibility //
            (document.activeElement as HTMLElement)?.blur()
            this.dialog.open(FormPreviewComponent, { data: { procedureFormId: this.procedureFormId }, panelClass: "no-dialog-borders" })
        }
    }

    restoreStateSelection(selectionId: string | null) {
        if (selectionId == null) {
            this.previewComponent.clearSelection()
            return
        }

        if (selectionId == this.sectionService.sectionMetadata.id) {
            this.ddSupportService.elementSelected = null
            this.detailComponent.selectSection(this.sectionService.sectionMetadata)
            this.previewComponent.clearSelection()
            return
        }

        let widget = this.sectionService.findWidget(selectionId)
        if (widget) {
            this.ddSupportService.elementSelected = widget.widget
            this.detailComponent.selectWidget(widget)
            this.previewComponent.showSelected(widget.widget.item())
            return
        }

        let layout = this.sectionService.findLayout(selectionId)
        if (layout) {
            this.ddSupportService.elementSelected = layout.row
            this.detailComponent.selectLayout(layout)
            this.previewComponent.showSelected(layout.row.layout())
            return
        }

    }

    restoreState(slm: SectionLayoutMetadataBase): void {

        this.registryHistory = false

        let items = slm.items
        slm.items = []

        this.sectionService.clearState(slm)
        this.layoutComponent.clearState()

        let sectionIndex = 0

        items.forEach(item => {

            if (item instanceof RowLayoutMetadataBase) {
                let createdRow = this.layoutComponent.addRowWithMetadata(sectionIndex, null, null, item)
                this.createRow(item, <RowLayout> createdRow?.row)
                sectionIndex++
            } else if (item instanceof ExtendedLayoutMetadataBase) {

                let layoutItems = item.items
                item.items = []

                let layoutToCreate: Layout;

                switch (item.type) {
                    case LayoutTypeMetadata.Repeat: 
                        layoutToCreate = new RepeatLayout(<RepeatLayoutMetadataBase> item)
                        break
                    case LayoutTypeMetadata.Table:
                        layoutToCreate = new TableLayout(<TableLayoutMetadataBase> item)
                        break
                    case LayoutTypeMetadata.WebService:
                        layoutToCreate = new WebServiceLayout(<WebServiceLayoutMetadataBase> item)
                        break
                }

                let createdLayout = this.layoutComponent.addLayoutRow(sectionIndex, layoutToCreate!)

                let nextRow: RowLayout = createdLayout.row
                let lastRow: RowLayout | null = null

                layoutItems.forEach(rowItem => {
                    let currentCreatedRow: RowLayout | null = null
                    if (lastRow == null) {
                        currentCreatedRow = nextRow
                        lastRow = nextRow
                    } else {
                        let newCreatedRow = this.layoutComponent.addRow(sectionIndex, lastRow, <Layout> createdLayout?.layout, false)
                        currentCreatedRow = newCreatedRow.row
                        lastRow = newCreatedRow.row
                    }

                    this.createRow(<RowLayoutMetadataBase>rowItem, currentCreatedRow)
                    sectionIndex++
                })

            }

        })

        this.restoreStateSelection(this.sectionHistoryService.selectionId())
        this.registryHistory = true

        this.changeDetectorRef.detectChanges()
        this.previewComponent?.clean()
    }

    private createRow(row: RowLayoutMetadataBase, createdRow: RowLayout) {
        let oldItems = row.items
        row.items = []

        let rowIndex = 0

        oldItems.forEach(item => {
            let toolRow = new RowLayout() //simulate a tool row layout
            let myTool = MyToolFactory.newTool(<ItemTypeMetadata> ToItemTypeMetadata(item.type), this.sectionService, item)

            myTool.sectionMetadataService = this.sectionService
            toolRow.widgets = [myTool]
            let cw = this.layoutComponent?.createWidget(toolRow, createdRow, 0, rowIndex, false)
            rowIndex++
        })
    }

    availableLines(): number | null {
        if (this.layoutComponent) {
            return this.layoutComponent.availableLines()
        }
        return null
    }


    onStartDrag($event: CdkDragStart) {
        this.previewComponent.clearSelection()
        this.ddWidget = !this.ddWidget
    }

    onEndDrag($event: CdkDragEnd) {
        this.ddWidget = !this.ddWidget
    }

    onAddTool($event: DDElement, index?: number) {

        if (this.layoutComponent.availableLines() <= 0) {
            this.snack.open("No hi han linies disponibles")
            return
        }

        let positionIndex = this.layoutComponent.currentRows.length
        if (index != undefined) {
            positionIndex = index
        }

        if ($event.name() == "Repeat" || $event.name() == "Table" || $event.name() == "WebService") {

            let layoutToCreate: Layout | null = null

            switch ($event.name()) {
                case "Repeat": 
                    layoutToCreate = new RepeatLayout()
                    break
                case "Table":
                    layoutToCreate = new TableLayout()
                    break
                case "WebService":
                    layoutToCreate = new WebServiceLayout()
                    break
            }

            if (layoutToCreate != null) {
                this.layoutComponent.addLayoutRow(positionIndex, layoutToCreate)
                return
            }
        }

        let toolRow = new RowLayout()
        toolRow.widgets = [$event]

        let createdRow = this.layoutComponent.addRow(positionIndex, null, null)
        this.layoutComponent.createWidget(toolRow, createdRow.row, 0, 0, false)
        this.layoutComponent.moveScroll(createdRow.row.id())
    }

    onRequestAddTool(requestedAddTool: RequestedAddTool) {
        let dialogRef = this.detailComponent.showToolsToAdd()
        dialogRef.afterClosed().subscribe(result => {
            if (result == null) {
                return
            }

            if (requestedAddTool.hasLayout) {
                this.onAddTool(result, requestedAddTool.index)
                return
            }

            let createdRow = this.layoutComponent.addRow(requestedAddTool.index + 1, requestedAddTool.afterRow, requestedAddTool.layout, false)
            let toolRow = new RowLayout()
            toolRow.widgets = [result]

            this.layoutComponent.dropIntern(createdRow.row, toolRow, 0, 0, "tools")
        })
    }

    onSectionChange(change: MetadataChange, recordOnHistory: boolean = true) {
        if (this.registryHistory && recordOnHistory) {
            this.previewComponent?.refreshChange(change)
            this.sectionHistoryService.do(this.sectionService.sectionMetadata, this.ddSupportService.elementSelected)
        }
    }

    onRecordOnHistory(doIt: boolean) {
        if (this.registryHistory && doIt) {
            this.sectionHistoryService.do(this.sectionService.sectionMetadata, this.ddSupportService.elementSelected)
        }
    }

     onSectionChangeWithRefresh(change: MetadataChange) {
        if (this.registryHistory) {
            this.previewComponent?.refreshChange(change)
            this.sectionHistoryService.do(this.sectionService.sectionMetadata, this.ddSupportService.elementSelected)
        }
    }

    onSelectedWidget(selectedWidget: SelectedWidget) {
        this.detailComponent.selectWidget(selectedWidget)
        this.previewComponent.showSelected(selectedWidget.widget.item())
    }

    onSelectedRow(selectedRow: SelectedRow) {
        this.detailComponent.selectToolTab()
        this.previewComponent.clearSelection()
    }

    onSelectedLayout(selectedLayout: SelectedLayout) {
        this.detailComponent.selectLayout(selectedLayout)
        this.previewComponent.showSelected(selectedLayout.row.layout())
    }

    onSelectedSection(selectedLayout: boolean) {
        this.ddSupportService.elementSelected = this.sectionService.sectionMetadata.id
        this.detailComponent.selectSection(this.sectionService.sectionMetadata)
        this.previewComponent.clearSelection()
    }

    onScroll($event: any) {

    }

    ngOnDestroy(): void {
        if (this.spinnerSubscription) {
            this.spinnerSubscription.unsubscribe()
        }
    }

}
